import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Container,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  AccessTime as AccessTimeIcon,
  Send as SendIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Login as LoginIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import api from '../utils/api';

const EmailSentPage = () => {
  const { t } = useTranslation('emailVerification');
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [email, setEmail] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const [requestCount, setRequestCount] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [timeRemaining, setTimeRemaining] = useState('');

  useEffect(() => {
    // Get email and other data from navigation state or URL params
    const state = location.state;
    const urlParams = new URLSearchParams(location.search);

    const emailFromState = state?.email || urlParams.get('email');
    const expiresAtFromState = state?.expiresAt || urlParams.get('expiresAt');
    const requestCountFromState = state?.requestCount || urlParams.get('requestCount');

    if (emailFromState) {
      setEmail(emailFromState);
    } else {
      // If no email provided, redirect to email verification
      navigate('/email-verification');
      return;
    }

    if (expiresAtFromState) {
      setExpiresAt(new Date(expiresAtFromState));
    }

    if (requestCountFromState) {
      setRequestCount(parseInt(requestCountFromState) || 1);
    }
  }, [location.state, location.search, navigate]);

  // Update time remaining every minute
  useEffect(() => {
    if (!expiresAt) return;

    const updateTimeRemaining = () => {
      const now = new Date();
      const timeDiff = expiresAt.getTime() - now.getTime();

      if (timeDiff <= 0) {
        setTimeRemaining(t('common.expired'));
      } else {
        const hours = Math.floor(timeDiff / (1000 * 60 * 60));
        const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

        if (hours > 0) {
          setTimeRemaining(`${hours}h ${minutes}m`);
        } else {
          setTimeRemaining(`${minutes}m`);
        }
      }
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 60000);

    return () => clearInterval(interval);
  }, [expiresAt, t]);

  const handleResendEmail = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/email-verification/resend', {
        email: email.toLowerCase().trim()
      });

      if (response.data.success) {
        setSuccess(t('request.messages.resendSuccess'));
        setRequestCount(response.data.data?.requestCount || requestCount + 1);

        if (response.data.data?.expiresAt) {
          setExpiresAt(new Date(response.data.data.expiresAt));
        }
      }
    } catch (err) {
      console.error('Resend email error:', err);

      if (err.response?.status === 429) {
        setError(t('request.messages.rateLimited'));
      } else if (err.response?.status === 404) {
        setError(t('errors.notFound.message'));
      } else {
        setError(err.response?.data?.error || t('request.messages.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEmail = () => {
    navigate('/email-verification', {
      state: { email }
    });
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Paper
          elevation={8}
          sx={{
            p: isMobile ? 3 : 4,
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.95) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.3)'
          }}
        >
          {/* Header */}
          <Box textAlign="center" mb={4}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Box position="relative" display="inline-block">
                <EmailIcon
                  sx={{
                    fontSize: 64,
                    color: 'success.main',
                    mb: 2
                  }}
                />
                <CheckCircleIcon
                  sx={{
                    position: 'absolute',
                    bottom: 16,
                    right: -8,
                    fontSize: 24,
                    color: 'success.main',
                    backgroundColor: 'white',
                    borderRadius: '50%'
                  }}
                />
              </Box>
            </motion.div>

            <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
              {t('sent.title')}
            </Typography>

            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              {t('sent.subtitle')}
            </Typography>
          </Box>

          {/* Email Info */}
          <Box mb={4} textAlign="center">
            <Typography
              variant="body1"
              sx={{ mb: 2 }}
              dangerouslySetInnerHTML={{
                __html: t('sent.message', { email })
              }}
            />

            <Box display="flex" justifyContent="center" gap={1} flexWrap="wrap" mb={2}>
              <Chip
                icon={<EmailIcon />}
                label={email}
                color="primary"
                variant="outlined"
              />

              {expiresAt && timeRemaining && (
                <Chip
                  icon={<AccessTimeIcon />}
                  label={t('sent.status.expiresAt', { time: timeRemaining })}
                  color={timeRemaining === t('common.expired') ? 'error' : 'info'}
                  variant="outlined"
                />
              )}

              <Chip
                icon={<SendIcon />}
                label={t('sent.status.requestCount', { count: requestCount })}
                color="default"
                variant="outlined"
              />
            </Box>
          </Box>

          {/* Alerts */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {/* Instructions */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon color="info" />
                {t('sent.instructions.title')}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <Typography variant="h6" color="primary">1</Typography>
                  </ListItemIcon>
                  <ListItemText primary={t('sent.instructions.step1')} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Typography variant="h6" color="primary">2</Typography>
                  </ListItemIcon>
                  <ListItemText primary={t('sent.instructions.step2')} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Typography variant="h6" color="primary">3</Typography>
                  </ListItemIcon>
                  <ListItemText primary={t('sent.instructions.step3')} />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* Troubleshooting */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon color="warning" />
                {t('sent.troubleshooting.title')}
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="action" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={t('sent.troubleshooting.checkSpam')} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="action" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={t('sent.troubleshooting.correctEmail')} />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="action" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={t('sent.troubleshooting.resend')} />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* Actions */}
          <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
            <Button
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
              onClick={handleResendEmail}
              sx={{ px: 4, py: 1.5 }}
            >
              {loading ? t('common.loading') : t('sent.actions.resend')}
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<ArrowBackIcon />}
              onClick={handleChangeEmail}
              sx={{ px: 4, py: 1.5 }}
            >
              {t('sent.actions.changeEmail')}
            </Button>

            <Button
              variant="text"
              size="large"
              startIcon={<LoginIcon />}
              onClick={handleBackToLogin}
              sx={{ px: 4, py: 1.5 }}
            >
              {t('sent.actions.backToLogin')}
            </Button>
          </Box>

          {/* Footer */}
          <Divider sx={{ my: 3 }} />
          <Box textAlign="center">
            <Typography variant="body2" color="text.secondary">
              {t('common.footer')}
            </Typography>
          </Box>
        </Paper>
      </motion.div>
    </Container>
  );
};

export default EmailSentPage;